import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  TableCellLayout,
  Button,
  Input,
  Spinner,
  useTableFeatures,
  useTableSort,
  createTableColumn,
} from "@fluentui/react-components";
import { CiFilter } from "react-icons/ci";
import { DeleteRegular, Add20Filled, EditRegular } from "@fluentui/react-icons";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import "../CSS/ListGroupsComponent.css";
import { getAccessToken } from "../../Services/GraphServices";
import { DeleteGroup, getAllGroups } from "../../Services/HeedifyServices";
import { ConfirmAlert } from "../GenericComponent/ConfirmAlert";
import { useNavigate } from "react-router-dom";

import { useAbility } from "../../Contexts/AbilityContext";
import { useNotification } from '../GenericComponent/NotificationSystem';
const ListGroupsComponent = (props) => {
  const { instance, accounts } = useMsal();
  const activeAccount = accounts[0];
  const userId = activeAccount?.localAccountId;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [groupsRows, setGroupsRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [deletingGroupId, setDeletingGroupId] = useState(null);
  const [filterVisible, setFilterVisible] = useState({
    NAME: false,
    SCOPE: false,
    AGENTS: false,
  });

  const [filters, setFilters] = useState({
    NAME: "",
    SCOPE: "",
    AGENTS: "",
  });
  const [agentCounts, setAgentCounts] = useState({});
  const [loadingCounts, setLoadingCounts] = useState(false);
  // 🔐 Ability pour permissions
  const ability = useAbility();
  const { showSuccess, showError } = useNotification();

  // 📊 Récupérer et compter les agents pour chaque groupe
  const refreshAgentCounts = async () => {
    if (!props.ST_GROUPS || props.ST_GROUPS.length === 0) return;
    
    setLoadingCounts(true);
    try {
      const token = await getAccessToken(instance, activeAccount);
      const counts = {};
      
      // Récupérer tous les scopes uniques
      const scopes = [...new Set(props.ST_GROUPS.map(g => g.scope))];
      
      // Récupérer les groupes pour chaque scope
      for (const scope of scopes) {
        const groups = await getAllGroups(props.ACTIVE_TENANT, scope, userId, token);
        groups.forEach(group => {
          counts[group.id] = group.agentsIds?.length || 0;
        });
      }
      
      setAgentCounts(counts);
    } catch (error) {
      console.error("Erreur lors du comptage des agents:", error);
    } finally {
      setLoadingCounts(false);
    }
  };

  // ⚡ Initialisation des rows avec permissions
  useEffect(() => {
    if (!props.ST_GROUPS || !ability) return;

    const groupsWithPermissions = props.ST_GROUPS.map((group) => {
      const scope = group.scope;
      
      // 🎯 Calculer toutes les permissions pour ce groupe
      const permissions = {
        main: {
          canCreate: ability.can("create", `Group.main@${scope}`),
          canRead: ability.can("read", `Group.main@${scope}`),
          canUpdate: ability.can("update", `Group.main@${scope}`),
        canDelete: ability.can("delete", `Group.main@${scope}`),
        }
      };
      
      return {
        ...group,
        permissions,
      };
    });

    setGroupsRows(groupsWithPermissions);
    setFilteredRows(groupsWithPermissions.filter(g => g.permissions.main.canRead));
    
    // Récupérer le count des agents
    refreshAgentCounts();
  }, [props.ST_GROUPS, ability]);
  // --- Filtering ---
  const applyFilters = (rows, filters) => {
    return rows.filter((group) => {
      const matchName = group.groupName
        ?.toLowerCase()
        .includes(filters.NAME.toLowerCase());
      const matchScope = group.scope
        ?.toLowerCase()
        .includes(filters.SCOPE.toLowerCase());

      const agentsCount = group.agentsIds?.length || 0;
      const matchAgents = filters.AGENTS === "" || agentsCount.toString().includes(filters.AGENTS);

      return matchName && matchScope && matchAgents;
    });
  };

  useEffect(() => {
    const newFiltered = applyFilters(groupsRows, filters);
    setFilteredRows(newFiltered);
  }, [filters, groupsRows]);

  // --- Sorting ---
  const columns = [
    createTableColumn({
      columnId: "NAME",
      compare: (a, b) => a.groupName?.localeCompare(b.groupName || "") ?? 0,
    }),
    createTableColumn({
      columnId: "SCOPE",
      compare: (a, b) => a.scope?.localeCompare(b.scope || "") ?? 0,
    }),
    createTableColumn({
      columnId: "AGENTS",
      compare: (a, b) => (a.agentsIds?.length || 0) - (b.agentsIds?.length || 0),
    }),
    createTableColumn({ columnId: "ACTION" }),
  ];

  const {
    getRows,
    sort: { getSortDirection, toggleColumnSort, sort },
  } = useTableFeatures({ columns, items: filteredRows }, [
    useTableSort({
      defaultSortState: { sortColumn: "NAME", sortDirection: "ascending" },
    }),
  ]);

  const rows = useMemo(() => sort(getRows()), [sort, getRows]);

  const headerSortProps = (columnId) => ({
    onClick: (e) => toggleColumnSort(e, columnId),
    sortDirection: getSortDirection(columnId),
  });

  const handleFilterChange = (columnKey, value) => {
    setFilters((prev) => ({ ...prev, [columnKey]: value }));
  };

  const toggleFilterVisibility = (column) => {
    setFilterVisible((prev) => ({ ...prev, [column]: !prev[column] }));
  };
  // --- CRUD Handlers ---
  const removeGroup = (key, groupId, scope) => {
    setDeletingGroupId(groupId);
    return getAccessToken(instance, activeAccount)
      .then((token) => {
        return DeleteGroup(props.ACTIVE_TENANT, scope, groupId, userId, token).then(
          (response) => {
            if (response?.success === true && response?.message === "Deleted") {
              props.setGroups(props.ST_GROUPS.filter((g) => g.id !== groupId));
              setGroupsRows((prev) => prev.filter((_, i) => i !== key));
              showSuccess(t("GROUP_DELETED_SUCCESS"));
            } else {
              const errorMsg = response?.message || "Erreur inconnue";
              console.error("Échec suppression groupe:", response);
              showError(t("GROUP_DELETE_FAILED"), errorMsg);
            }
          }
        );
      })
      .catch((error) => {
        console.error("Erreur de suppression :", error);
        showError(t("GROUP_DELETE_ERROR"), error?.message);
      })
      .finally(() => {
        setDeletingGroupId(null);
        setSelectedGroup(null);
      });
  };

  const confirmRemoveGroup = (group, index) => {
    setSelectedGroup({ ...group, key: index });
  };

  const addGroup = () => {
    navigate(`/groups/newGroup/null`);
  };

  const selectGroup = (groupId, scope) => {
    navigate(`/groups/${groupId}/${scope}`);
  };

  // 🔐 Vérifier les permissions via CASL ability plutôt que via les données
  // Vérifier si l'utilisateur peut créer des groupes dans au moins un scope
  const hasAnyCreatePermission = props.SCOPES_NAMES?.some(scope => 
    ability?.can("create", `Group.main@${scope}`)
  );
  

  // --- Render ---
  return (
    <>
      <div >
      <div className="group-list-header-container">
        <h2>{t("GROUPS_LIST")}</h2>
        <Button
          appearance="primary"
          onClick={addGroup}
          className="group-list-button-style"
          disabled={!hasAnyCreatePermission}
        >
          <Add20Filled /> {t("ADD_NEW_GROUP")}
        </Button>
      </div>

      <div className="group-list-main-container">
        <div className="group-list-table-wrapper">
          <Table
            role="grid"
            aria-label="Groups table with filters and sorting"
            className="group-list-table-style"
            sortable
          >
            <TableHeader>
              <TableRow className="group-list-header-style">
                <TableHeaderCell
                  className="group-list-cell-bold"
                  {...headerSortProps("NAME")}
                >
                  <div className="group-list-header-name-filter">
                    <span>{t("NAME")}</span>
                    <CiFilter
                      className="filter-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFilterVisibility("NAME");
                      }}
                    />
                    {filterVisible.NAME && (
                      <Input
                        className="group-list-filter-input-inline"
                        value={filters.NAME}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e, data) => handleFilterChange("NAME", data.value)}
                        placeholder={t("FILTER")}
                        size="small"
                      />
                    )}
                  </div>
                </TableHeaderCell>

                <TableHeaderCell
                  className="group-list-cell-bold"
                  {...headerSortProps("SCOPE")}
                >
                  <div className="group-list-header-name-filter">
                    <span>{t("SCOPE")}</span>
                    <CiFilter
                      className="filter-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFilterVisibility("SCOPE");
                      }}
                    />
                    {filterVisible.SCOPE && (
                      <Input
                        className="group-list-filter-input-inline"
                        value={filters.SCOPE}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e, data) => handleFilterChange("SCOPE", data.value)}
                        placeholder={t("FILTER")}
                        size="small"
                      />
                    )}
                  </div>
                </TableHeaderCell>

                <TableHeaderCell
                  className="group-list-cell-bold"
                  {...headerSortProps("AGENTS")}
                >
                  <div className="group-list-header-name-filter">
                    <span>{t("AGENTS")}</span>
                    <CiFilter
                      className="filter-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFilterVisibility("AGENTS");
                      }}
                    />
                    {filterVisible.AGENTS && (
                      <Input
                        className="group-list-filter-input-inline group-list-filter-agents"
                        value={filters.AGENTS}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e, data) => handleFilterChange("AGENTS", data.value)}
                        placeholder={t("FILTER")}
                        size="small"
                        type="number"
                      />
                    )}
                  </div>
                </TableHeaderCell>

                <TableHeaderCell className="group-list-cell-bold group-list-action-header">
                  {t("ACTION")}
                </TableHeaderCell>
              </TableRow>
            </TableHeader>

            <TableBody>
              {rows.map(({ item, rowId }) => {
                return (
                  <TableRow key={rowId}>
                    <TableCell>
                      <TableCellLayout>{item.groupName}</TableCellLayout>
                    </TableCell>
                    <TableCell>
                      <TableCellLayout>{item.scope}</TableCellLayout>
                    </TableCell>
                    <TableCell>
                      <TableCellLayout>
                       
                         {agentCounts[item.id] !== undefined ? agentCounts[item.id] : (item.agentsIds?.length || "0")}
                       
                     
                      </TableCellLayout>
                    </TableCell>
                    <TableCell className="group-list-action-cell">
                      <Button
                        appearance="subtle"
                        icon={<EditRegular />}
                        aria-label="Edit"
                        onClick={() => selectGroup(item.id, item.scope)}
                        disabled={!item.permissions?.main?.canUpdate}
                      />
                      <Button
                        appearance="subtle"
                        icon={deletingGroupId === item.id ? <Spinner size="tiny" /> : <DeleteRegular />}
                        aria-label="Delete"
                        onClick={() => confirmRemoveGroup(item, rowId)}
                        disabled={!item.permissions?.main?.canDelete || deletingGroupId === item.id}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      {selectedGroup && (
        <ConfirmAlert
          open={!!selectedGroup}
          name={selectedGroup.groupName}
          onConfirm={() =>
            removeGroup(selectedGroup.key, selectedGroup.id, selectedGroup.scope)
          }
          onCancel={() => setSelectedGroup(null)}
        />
      )}
      </div>
    </>
  );
};

export default ListGroupsComponent;
